import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { Tooltip } from '../components/ui/Tooltip';
import { DatePicker } from '../components/ui/DatePicker';
import { Plus, Filter, Download, Eye, Edit, Search, Receipt, CheckCircle2, Calendar, X, Save, Mail, List as ListIcon, Euro, Trash2, AlertCircle, FileText, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data with new statuses and Nota di credito
const initialInvoicesData = Array.from({
  length: 50
}, (_, i) => {
  let type = 'Fattura';
  let codePrefix = 'F';
  if (i % 5 === 2) {
    type = 'Nota di credito';
    codePrefix = 'NC';
  } else if (i % 2 !== 0) {
    type = 'Proforma';
    codePrefix = 'P';
  }
  return {
    id: i + 1,
    code: `${codePrefix}-2025/${String(i + 1).padStart(3, '0')}`,
    type: type,
    client: `Azienda ${i} SRL`,
    date: '31/01/2025',
    amount: `€ ${1200 + i * 50}`,
    status: ['da_pianificare', 'pianificato', 'proforma', 'fatturato', 'pagato', 'sollecitare', 'archiviato'][i % 7],
    dueDate: '28/02/2025',
    approval: i % 4 === 0 ? 'pending' : 'approved',
    sollecito1: i % 7 === 5,
    sollecito2: i % 7 === 5 && i % 2 === 0,
    sollecito3: false,
    contractNumber: `CTR-2024/${String(i).padStart(3, '0')}`,
    proformaNumber: type === 'Fattura' ? `P-2025/${String(i).padStart(3, '0')}` : '',
    invoiceNumber: type === 'Nota di credito' ? `F-2025/${String(i - 1).padStart(3, '0')}` : '',
    withVat: true,
    paymentDate: i % 7 === 4 ? '2025-02-15' : '' // If status is pagato
  };
});
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'type',
  label: 'Tipo'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'date',
  label: 'Data emiss.'
}, {
  key: 'amount',
  label: 'Importo'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'approval',
  label: 'Avallo'
}, {
  key: 'dueDate',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const statusLabels: Record<string, string> = {
  da_pianificare: 'Da fare',
  pianificato: 'Pianificato',
  proforma: 'Proforma',
  fatturato: 'Effettuata',
  pagato: 'Pagata',
  sollecitare: 'Sollecitare',
  archiviato: 'Archiviato',
  revisione_pagamento: 'Revisione pagamento',
  non_pagata: 'Non pagata',
  recupero_crediti: 'Recupero crediti'
};
const statusColors: Record<string, string> = {
  da_pianificare: 'neutral',
  pianificato: 'info',
  proforma: 'warning',
  fatturato: 'primary',
  pagato: 'success',
  sollecitare: 'danger',
  archiviato: 'neutral',
  revisione_pagamento: 'warning',
  non_pagata: 'danger',
  recupero_crediti: 'danger'
};
export function InvoicingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [invoicesData, setInvoicesData] = useState(initialInvoicesData);
  const [formData, setFormData] = useState<any>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(invoicesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['code', 'client']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  // Auto-set payment date when status becomes 'pagato'
  useEffect(() => {
    if (formData.status === 'pagato' && !formData.paymentDate) {
      setFormData(prev => ({
        ...prev,
        paymentDate: new Date().toISOString().split('T')[0]
      }));
    }
  }, [formData.status]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    // Initialize form data with item values
    setFormData({
      ...item,
      dueDateType: 'Specifica' // Default for existing items
    });
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      type: 'Fattura',
      status: 'da_pianificare',
      sendEmailOnExpiry: true,
      sendEmailToSEF: true,
      withVat: true,
      dueDateType: 'Immediata',
      sollecito1: false,
      sollecito2: false,
      sollecito3: false
    });
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }, 1500);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const getStatusOptions = (type: string) => {
    if (type === 'Proforma' || type === 'Nota di credito') {
      return [{
        value: 'da_pianificare',
        label: 'Da fare'
      }, {
        value: 'fatturato',
        label: 'Effettuata'
      }, {
        value: 'archiviato',
        label: 'Archiviata'
      }];
    }
    // Fattura
    return [{
      value: 'da_pianificare',
      label: 'Da fare'
    }, {
      value: 'fatturato',
      label: 'Effettuata'
    }, {
      value: 'revisione_pagamento',
      label: 'Revisione pagamento'
    }, {
      value: 'pagato',
      label: 'Pagata'
    }, {
      value: 'non_pagata',
      label: 'Non pagata'
    }, {
      value: 'sollecitare',
      label: 'Sollecitare'
    }, {
      value: 'recupero_crediti',
      label: 'Recupero crediti'
    }, {
      value: 'archiviato',
      label: 'Archiviata'
    }];
  };
  // Helper to get filter status options based on selected type
  const getFilterStatusOptions = () => {
    if (!filters.type) {
      // Combine all unique statuses if no type selected
      const allStatuses = new Set<string>();
      getStatusOptions('Fattura').forEach(opt => allStatuses.add(opt.value));
      getStatusOptions('Proforma').forEach(opt => allStatuses.add(opt.value));
      // Map back to option objects
      return Array.from(allStatuses).map(status => ({
        value: status,
        label: statusLabels[status] || status
      }));
    }
    return getStatusOptions(filters.type);
  };
  const getCodeLabel = (type: string) => {
    switch (type) {
      case 'Fattura':
        return 'Numero fattura';
      case 'Proforma':
        return 'Numero proforma';
      case 'Nota di credito':
        return 'Numero nota di credito';
      default:
        return 'Numero';
    }
  };
  const getProgressiveType = (type: string) => {
    switch (type) {
      case 'Fattura':
        return 'invoice';
      case 'Proforma':
        return 'proforma';
      case 'Nota di credito':
        return 'credit_note';
      default:
        return 'invoice';
    }
  };
  return <Layout data-id="element-3939">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-3940">
        <div data-id="element-3941">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-3942">
            Fatturazione e pagamenti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-3943">
            Gestione ciclo attivo, avalli e recupero crediti.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3944">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-3946" />} data-id="element-3945">
            Esporta elenco
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-3948" />} data-id="element-3947">
            Nuovo documento
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-3949">
        <div className="p-4 flex flex-col gap-4" data-id="element-3950">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-3951">
            <div className="flex-1" data-id="element-3952">
              <Input placeholder="Cerca documento, cliente..." startContent={<Search className="h-4 w-4" data-id="element-3954" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-3953" />
            </div>
            <div className="flex gap-2" data-id="element-3955">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-3957" />} data-id="element-3956">
                Filtri{' '}
                {Object.keys(filters).filter(k => filters[k]).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-3958">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="invoicing" lockedColumns={['code']} data-id="element-3959" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-3960">
              <Select label="Tipo" value={filters.type || ''} onChange={e => {
            setFilter('type', e.target.value);
            // Reset status if not valid for new type
            if (e.target.value && filters.status) {
              const validStatuses = getStatusOptions(e.target.value).map(o => o.value);
              if (!validStatuses.includes(filters.status)) {
                setFilter('status', '');
              }
            }
          }} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Fattura',
            label: 'Fattura'
          }, {
            value: 'Proforma',
            label: 'Proforma'
          }, {
            value: 'Nota di credito',
            label: 'Nota di credito'
          }]} data-id="element-3961" />
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...getFilterStatusOptions()]} data-id="element-3962" />
              <div className="col-span-full flex justify-end" data-id="element-3963">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-3965" />} data-id="element-3964">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-3966">
        <div className="overflow-x-auto" data-id="element-3967">
          <table className="w-full text-left border-collapse table-auto" data-id="element-3968">
            <thead data-id="element-3969">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3970">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-3971">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-3972">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3973">
                  {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-3974">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-3975">
                      <span className={`text-xs font-medium px-2 py-1 rounded 
                          ${item.type === 'Fattura' ? 'bg-blue-50 text-blue-700' : item.type === 'Nota di credito' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-3976">
                        {item.type}
                      </span>
                    </td>}
                  {visibleColumns.includes('client') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-3977">
                      {item.client}
                    </td>}
                  {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3978">
                      {item.date}
                    </td>}
                  {visibleColumns.includes('amount') && <td className="px-4 py-4 text-sm font-mono text-slate-900" data-id="element-3979">
                      {item.amount}
                    </td>}
                  {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-3980">
                      <Badge variant={statusColors[item.status] as any || 'neutral'} data-id="element-3981">
                        {statusLabels[item.status] || item.status}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('approval') && <td className="px-4 py-4" data-id="element-3982">
                      {item.approval === 'pending' && <Badge variant="warning" data-id="element-3983">Da avallare</Badge>}
                      {item.approval === 'approved' && <Badge variant="success" data-id="element-3984">Avallato</Badge>}
                    </td>}
                  {visibleColumns.includes('dueDate') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3985">
                      {item.dueDate}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-3986">
                      <div className="flex items-center gap-1" data-id="element-3987">
                        <Tooltip content="Visualizza documento" data-id="element-3988">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-3989">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-3990" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Modifica documento" data-id="element-3991">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-3992">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-3993" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Elimina documento" data-id="element-3994">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-3995">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-3996" />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-3997">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3998" />
        </div>
      </Card>

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio documento" size="2xl" data-id="element-3999">
        {selectedItem && <div className="space-y-8" data-id="element-4000">
            <section data-id="element-4001">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4002">
                <Receipt className="h-4 w-4" data-id="element-4003" /> Dati documento
              </h4>
              <div className="grid grid-cols-2 gap-6" data-id="element-4004">
                <div data-id="element-4005">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4006">
                    {getCodeLabel(selectedItem.type)}
                  </span>
                  <p className="font-mono font-medium" data-id="element-4007">{selectedItem.code}</p>
                </div>
                <div data-id="element-4008">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4009">
                    Tipo documento
                  </span>
                  <p className="font-medium" data-id="element-4010">{selectedItem.type}</p>
                </div>
                <div data-id="element-4011">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4012">
                    Cliente
                  </span>
                  <p className="font-medium" data-id="element-4013">{selectedItem.client}</p>
                </div>
                <div data-id="element-4014">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4015">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-4016">
                    <Badge variant={statusColors[selectedItem.status] as any || 'neutral'} data-id="element-4017">
                      {statusLabels[selectedItem.status] || selectedItem.status}
                    </Badge>
                  </div>
                </div>

                {selectedItem.type === 'Fattura' && selectedItem.status === 'sollecitare' && <div className="col-span-full bg-orange-50 p-4 rounded-xl border border-orange-200" data-id="element-4018">
                      <span className="text-xs font-semibold text-orange-600 uppercase block mb-2" data-id="element-4019">
                        Stato solleciti
                      </span>
                      <div className="flex gap-4" data-id="element-4020">
                        <div className="flex items-center gap-2" data-id="element-4021">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedItem.sollecito1 ? 'bg-orange-500 border-orange-500' : 'border-orange-300'}`} data-id="element-4022">
                            {selectedItem.sollecito1 && <CheckCircle2 className="w-3 h-3 text-white" data-id="element-4023" />}
                          </div>
                          <span className="text-sm text-orange-800" data-id="element-4024">
                            Sollecito 1
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-4025">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedItem.sollecito2 ? 'bg-orange-500 border-orange-500' : 'border-orange-300'}`} data-id="element-4026">
                            {selectedItem.sollecito2 && <CheckCircle2 className="w-3 h-3 text-white" data-id="element-4027" />}
                          </div>
                          <span className="text-sm text-orange-800" data-id="element-4028">
                            Sollecito 2
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-4029">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedItem.sollecito3 ? 'bg-orange-500 border-orange-500' : 'border-orange-300'}`} data-id="element-4030">
                            {selectedItem.sollecito3 && <CheckCircle2 className="w-3 h-3 text-white" data-id="element-4031" />}
                          </div>
                          <span className="text-sm text-orange-800" data-id="element-4032">
                            Sollecito 3
                          </span>
                        </div>
                      </div>
                    </div>}
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-4033"></div>

            <section data-id="element-4034">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4035">
                <FileText className="h-4 w-4" data-id="element-4036" /> Riferimenti e dettagli
              </h4>
              <div className="grid grid-cols-2 gap-6" data-id="element-4037">
                <div data-id="element-4038">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4039">
                    Numero di contratto
                  </span>
                  <p className="font-medium" data-id="element-4040">
                    {selectedItem.contractNumber || '-'}
                  </p>
                </div>

                {selectedItem.type === 'Fattura' && <div data-id="element-4041">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4042">
                      Numero di proforma
                    </span>
                    <p className="font-medium" data-id="element-4043">
                      {selectedItem.proformaNumber || '-'}
                    </p>
                  </div>}

                {selectedItem.type === 'Nota di credito' && <div data-id="element-4044">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4045">
                      Numero fattura
                    </span>
                    <p className="font-medium" data-id="element-4046">
                      {selectedItem.invoiceNumber || '-'}
                    </p>
                  </div>}

                <div data-id="element-4047">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4048">
                    Importo con IVA
                  </span>
                  <div className="mt-1" data-id="element-4049">
                    <Badge variant={selectedItem.withVat ? 'success' : 'neutral'} data-id="element-4050">
                      {selectedItem.withVat ? 'Sì' : 'No'}
                    </Badge>
                  </div>
                </div>
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-4051"></div>

            <section data-id="element-4052">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4053">
                <Euro className="h-4 w-4" data-id="element-4054" /> Dettagli economici
              </h4>
              <div className="grid grid-cols-2 gap-6" data-id="element-4055">
                <div data-id="element-4056">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4057">
                    Importo
                  </span>
                  <p className="font-mono font-bold text-seform-navy" data-id="element-4058">
                    {selectedItem.amount}
                  </p>
                </div>
                <div data-id="element-4059">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4060">
                    Data emissione
                  </span>
                  <p className="font-medium" data-id="element-4061">{selectedItem.date}</p>
                </div>
                <div data-id="element-4062">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4063">
                    Data scadenza
                  </span>
                  <p className="font-medium" data-id="element-4064">{selectedItem.dueDate}</p>
                </div>
                <div data-id="element-4065">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4066">
                    Data di pagamento
                  </span>
                  <p className="font-medium" data-id="element-4067">
                    {selectedItem.paymentDate || '-'}
                  </p>
                </div>
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-4068"></div>

            <section data-id="element-4069">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4070">
                <Mail className="h-4 w-4" data-id="element-4071" /> Notifiche automatiche
              </h4>
              <div className="space-y-2" data-id="element-4072">
                <div className="flex items-center gap-2" data-id="element-4073">
                  <div className={`w-2 h-2 rounded-full ${selectedItem.sendEmailOnExpiry !== false ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-4074"></div>
                  <span className="text-sm text-slate-700" data-id="element-4075">
                    Invia email automatica alla scadenza al cliente
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-4076">
                  <div className={`w-2 h-2 rounded-full ${selectedItem.sendEmailToSEF !== false ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-4077"></div>
                  <span className="text-sm text-slate-700" data-id="element-4078">
                    Invia notifica a SEF alla scadenza
                  </span>
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4079">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-4080">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-4082" />} data-id="element-4081">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica documento' : 'Nuovo documento'} size="2xl" data-id="element-4083">
        <AnimatePresence mode="wait" data-id="element-4084">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-4085">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4086">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-4087" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4088">
                Documento salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4089">
              <div className="space-y-8" data-id="element-4090">
                <section data-id="element-4091">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4092">
                    <Receipt className="h-4 w-4" data-id="element-4093" /> Dati documento
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4094">
                    {selectedItem ? <Input label="Codice" value={formData.code} disabled data-id="element-4095" /> : <ProgressiveNumberInput type={getProgressiveType(formData.type)} value={formData.code} onChange={val => setFormData({
                  ...formData,
                  code: val
                })} label={getCodeLabel(formData.type)} data-id="element-4096" />}

                    <Select label="Tipo documento" value={formData.type || 'Fattura'} onChange={e => {
                  const newType = e.target.value;
                  // Reset status when type changes
                  const newStatusOptions = getStatusOptions(newType);
                  setFormData({
                    ...formData,
                    type: newType,
                    status: newStatusOptions[0].value
                  });
                }} options={[{
                  value: 'Fattura',
                  label: 'Fattura'
                }, {
                  value: 'Proforma',
                  label: 'Proforma'
                }, {
                  value: 'Nota di credito',
                  label: 'Nota di credito'
                }]} required data-id="element-4097" />

                    <SearchableSelect label="Cliente" value={formData.client || ''} onChange={e => setFormData({
                  ...formData,
                  client: e.target.value
                })} options={[{
                  value: 'Azienda 1 SRL',
                  label: 'Azienda 1 SRL'
                }, {
                  value: 'Azienda 2 SRL',
                  label: 'Azienda 2 SRL'
                }, {
                  value: 'Azienda 3 SRL',
                  label: 'Azienda 3 SRL'
                }, {
                  value: 'Azienda 4 SRL',
                  label: 'Azienda 4 SRL'
                }, {
                  value: 'Azienda 5 SRL',
                  label: 'Azienda 5 SRL'
                }]} required data-id="element-4098" />

                    <Select label="Stato" value={formData.status || 'da_pianificare'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={getStatusOptions(formData.type)} required data-id="element-4099" />

                    {formData.type === 'Fattura' && formData.status === 'sollecitare' && <div className="col-span-full bg-orange-50 p-4 rounded-xl border border-orange-200" data-id="element-4100">
                          <span className="text-xs font-semibold text-orange-600 uppercase block mb-3" data-id="element-4101">
                            Stato solleciti
                          </span>
                          <div className="flex gap-6" data-id="element-4102">
                            <label className="flex items-center gap-2 cursor-pointer" data-id="element-4103">
                              <input type="checkbox" checked={formData.sollecito1} onChange={e => setFormData({
                        ...formData,
                        sollecito1: e.target.checked,
                        sollecito2: e.target.checked ? formData.sollecito2 : false,
                        sollecito3: e.target.checked ? formData.sollecito3 : false
                      })} className="rounded border-orange-300 text-orange-500 focus:ring-orange-500" data-id="element-4104" />
                              <span className="text-sm font-medium text-orange-900" data-id="element-4105">
                                Sollecito 1
                              </span>
                            </label>

                            <label className={`flex items-center gap-2 ${!formData.sollecito1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-4106">
                              <input type="checkbox" checked={formData.sollecito2} disabled={!formData.sollecito1} onChange={e => setFormData({
                        ...formData,
                        sollecito2: e.target.checked,
                        sollecito3: e.target.checked ? formData.sollecito3 : false
                      })} className="rounded border-orange-300 text-orange-500 focus:ring-orange-500" data-id="element-4107" />
                              <span className="text-sm font-medium text-orange-900" data-id="element-4108">
                                Sollecito 2
                              </span>
                            </label>

                            <label className={`flex items-center gap-2 ${!formData.sollecito2 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-4109">
                              <input type="checkbox" checked={formData.sollecito3} disabled={!formData.sollecito2} onChange={e => setFormData({
                        ...formData,
                        sollecito3: e.target.checked
                      })} className="rounded border-orange-300 text-orange-500 focus:ring-orange-500" data-id="element-4110" />
                              <span className="text-sm font-medium text-orange-900" data-id="element-4111">
                                Sollecito 3
                              </span>
                            </label>
                          </div>
                        </div>}
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-4112"></div>

                <section data-id="element-4113">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4114">
                    <FileText className="h-4 w-4" data-id="element-4115" /> Riferimenti e dettagli
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4116">
                    <Input label="Numero di contratto" placeholder="Inserisci numero contratto" value={formData.contractNumber || ''} onChange={e => setFormData({
                  ...formData,
                  contractNumber: e.target.value
                })} data-id="element-4117" />

                    {formData.type === 'Fattura' && <Input label="Numero di proforma" placeholder="Inserisci numero proforma" value={formData.proformaNumber || ''} onChange={e => setFormData({
                  ...formData,
                  proformaNumber: e.target.value
                })} data-id="element-4118" />}

                    {formData.type === 'Nota di credito' && <Input label="Numero fattura" placeholder="Inserisci numero fattura" value={formData.invoiceNumber || ''} onChange={e => setFormData({
                  ...formData,
                  invoiceNumber: e.target.value
                })} data-id="element-4119" />}

                    <div className="flex flex-col gap-1.5" data-id="element-4120">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-4121">
                        Importo con IVA
                      </label>
                      <div className="flex items-center gap-2 h-[42px]" data-id="element-4122">
                        <span className={formData.withVat ? 'text-green-600 font-bold' : 'text-slate-400'} data-id="element-4123">
                          Sì
                        </span>
                        <button onClick={() => setFormData({
                      ...formData,
                      withVat: !formData.withVat
                    })} className={`w-10 h-5 rounded-full relative transition-colors ${formData.withVat ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-4124">
                          <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${formData.withVat ? 'left-6' : 'left-1'}`} data-id="element-4125" />
                        </button>
                        <span className={!formData.withVat ? 'text-red-600 font-bold' : 'text-slate-400'} data-id="element-4126">
                          No
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-4127"></div>

                <section data-id="element-4128">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4129">
                    <Euro className="h-4 w-4" data-id="element-4130" /> Dettagli economici
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4131">
                    <Input label="Importo (€)" type="number" placeholder="0.00" value={formData.amount?.replace('€ ', '') || ''} onChange={e => setFormData({
                  ...formData,
                  amount: `€ ${e.target.value}`
                })} required data-id="element-4132" />
                    <Input label="Data emissione" type="date" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-4133" />

                    <div className="flex flex-col gap-2" data-id="element-4134">
                      <Select label="Data scadenza" value={formData.dueDateType || 'Immediata'} onChange={e => setFormData({
                    ...formData,
                    dueDateType: e.target.value
                  })} options={[{
                    value: 'Immediata',
                    label: 'Immediata'
                  }, {
                    value: 'Specifica',
                    label: 'Specifica'
                  }, {
                    value: 'Inizio mese',
                    label: 'Inizio mese'
                  }]} data-id="element-4135" />
                      {formData.dueDateType === 'Specifica' && <Input type="date" value={formData.dueDate || ''} onChange={e => setFormData({
                    ...formData,
                    dueDate: e.target.value
                  })} data-id="element-4136" />}
                    </div>

                    <Input label="Data di pagamento" type="date" value={formData.paymentDate || ''} onChange={e => setFormData({
                  ...formData,
                  paymentDate: e.target.value
                })} disabled={formData.status === 'pagato'} description={formData.status === 'pagato' ? 'Impostata automaticamente' : undefined} data-id="element-4137" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-4138"></div>

                <section data-id="element-4139">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4140">
                    <Mail className="h-4 w-4" data-id="element-4141" /> Notifiche automatiche
                  </h4>
                  <div className="space-y-3" data-id="element-4142">
                    <div className="flex items-center gap-2" data-id="element-4143">
                      <input type="checkbox" id="sendEmailOnExpiry" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailOnExpiry !== false} onChange={e => setFormData({
                    ...formData,
                    sendEmailOnExpiry: e.target.checked
                  })} data-id="element-4144" />
                      <label htmlFor="sendEmailOnExpiry" className="text-sm text-slate-700" data-id="element-4145">
                        Invia email automatica alla scadenza al cliente
                      </label>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-4146">
                      <input type="checkbox" id="sendEmailToSEF" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailToSEF !== false} onChange={e => setFormData({
                    ...formData,
                    sendEmailToSEF: e.target.checked
                  })} data-id="element-4147" />
                      <label htmlFor="sendEmailToSEF" className="text-sm text-slate-700" data-id="element-4148">
                        Invia notifica a SEF alla scadenza
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4149">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-4150">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-4152" />} data-id="element-4151">
                    Salva documento
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-4153">
        <AnimatePresence mode="wait" data-id="element-4154">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-4155">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4156">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-4157" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4158">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4159">
              <div className="space-y-6" data-id="element-4160">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-4161">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-4162" />
                  <div data-id="element-4163">
                    <p className="text-sm text-red-700 font-medium" data-id="element-4164">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-4165">
                      Il documento <strong data-id="element-4166">{itemToDelete?.code}</strong> verrà
                      eliminato definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-4167">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-4168">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-4170" />} data-id="element-4169">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}